import { WorkOrder } from "../models/workOrderTypes";

export interface BoardColumnBase {
  identity?: number;
  displayOrder?: number;
  workOrders: WorkOrder[];
}

export const toColumnArray = <T extends BoardColumnBase>(
  columns: T[] | Record<string, T> | null | undefined,
): T[] => (Array.isArray(columns) ? columns.slice() : Object.values(columns ?? {}));

export const dedupeColumnsByIdentity = <T extends BoardColumnBase>(
  columns: T[],
): T[] => {
  const seen = new Set<number>();
  return columns.filter((column) => {
    const identity = column.identity;
    if (identity == null) return true;
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

export const filterColumnsWithWorkOrders = <T extends BoardColumnBase>(
  columns: T[] | Record<string, T> | null | undefined,
): T[] =>
  dedupeColumnsByIdentity(toColumnArray(columns)).filter(
    (column) => column.workOrders.length > 0,
  );

export const getVisibleColumnEntries = <T extends BoardColumnBase>(
  columns: T[] | Record<string, T> | null | undefined,
): Array<[string, T]> => {
  const seen = new Set<number>();
  return toColumnArray(columns)
    .map((column, index) => [String(index), column] as [string, T])
    .filter(([, column]) => {
      if (column.workOrders.length === 0) return false;
      const identity = column.identity;
      if (identity == null) return true;
      if (seen.has(identity)) return false;
      seen.add(identity);
      return true;
    })
    .sort(
      ([, a], [, b]) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
    );
};

export const parseDroppableColumnIndex = (droppableId: string): number =>
  Number.parseInt(droppableId, 10);

export const updateColumnWorkOrders = <T extends BoardColumnBase>(
  columns: T[],
  columnIndex: number,
  workOrders: WorkOrder[],
): T[] =>
  columns.map((column, index) =>
    index === columnIndex ? { ...column, workOrders } : column,
  );

export const updateTwoColumnWorkOrders = <T extends BoardColumnBase>(
  columns: T[],
  sourceIndex: number,
  sourceWorkOrders: WorkOrder[],
  destIndex: number,
  destWorkOrders: WorkOrder[],
): T[] =>
  columns.map((column, index) => {
    if (index === sourceIndex) {
      return { ...column, workOrders: sourceWorkOrders };
    }
    if (index === destIndex) {
      return { ...column, workOrders: destWorkOrders };
    }
    return column;
  });
